import * as React from 'react';
import * as L from 'leaflet';
import { GeoJSON } from 'react-leaflet';
import { Feature, Polygon } from 'geojson';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import withStyles, {
  WithStyles,
  StyleRulesCallback
} from '@material-ui/core/styles/withStyles';

import 'leaflet-draw';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

import { sanitizePolygon, generateId } from '../utils';
import circleToPolygon from '../utils/circleToPolygon';
import { FarmMap, LinkButton, UploadKmzDialog } from '../components';

import { FarmProvider, LayoutToolsProvider, LayoutTools } from '../providers';
import NewFieldProvider, { MutateFn } from '../providers/NewFieldProvider';
import UpdateFieldProvider, {
  UpdateFn
} from '../providers/UpdateFieldProvider';
import DeleteFieldProvider, {
  DeleteFn
} from '../providers/DeleteFieldProvider';

import BackIcon from '@material-ui/icons/ArrowBack';
import CircleIcon from '@material-ui/icons/AllOut';
import PolygonIcon from '@material-ui/icons/BorderOuter';
import ClearIcon from '@material-ui/icons/Clear';
import SaveIcon from '@material-ui/icons/Save';
import CreateIcon from '@material-ui/icons/Create';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Typography as T,
  ListItemText,
  ListItemIcon,
  ListSubheader,
  Divider,
  ListItem,
  List,
  Snackbar,
  SnackbarContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton
} from '@material-ui/core';
import SidePanel from './SidePanel';

const styles: StyleRulesCallback = theme => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    height: '100vh'
  },
  panel: {
    backgroundColor: theme.palette.grey['200'],
    width: 340,
    padding: theme.spacing.unit * 3
  },
  list: {
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing.unit * 3
  },
  button: {
    marginBottom: theme.spacing.unit * 3
  },
  map: {
    flex: 1
  }
});

interface Action {
  type: 'create' | 'delete' | 'edit';
  field: Field;
}

interface State {
  tempAoi: Feature<Polygon, AoiProperties> | null;
  tempName: string | null;
  tempCrop: string;
  selected: Field | null;
  editedGeometry: PolygonGeometry | null;
  showUploadKmzDialog: boolean;
  showDeleteDialog: boolean;
  showUpdateDialog: boolean;
  lastAction: Action | null;
}

interface AoiProperties {
  id: string;
}

const startPolygon = (mapRef: L.Map | null, onFinish: Function) => {
  if (!mapRef) {
    throw new Error('No valid leaflet reference to start drawing');
  }

  const drawer = new (L as any).default.Draw.Polygon(mapRef);

  drawer.setOptions({
    icon: new L.DivIcon({
      iconSize: new L.Point(8, 8)
    })
  });

  mapRef.once((L as any).default.Draw.Event.CREATED, (e: any) => {
    if (e.layerType === 'polygon') {
      const feature = e.layer.toGeoJSON();
      feature.properties.id = generateId();
      feature.geometry.coordinates = sanitizePolygon(
        feature.geometry.coordinates
      );
      onFinish(feature);
    }
  });
  drawer.enable();
};

const startCircle = (mapRef: L.Map | null, onFinish: Function) => {
  if (!mapRef) {
    throw new Error('No valid leaflet reference to start drawing');
  }

  const drawer = new (L as any).default.Draw.Circle(mapRef);

  drawer.setOptions({
    icon: new L.DivIcon({
      iconSize: new L.Point(8, 8)
    })
  });

  mapRef.once((L as any).default.Draw.Event.CREATED, (e: any) => {
    if (e.layerType === 'circle') {
      const latLng: L.LatLngLiteral = e.layer.getLatLng();
      const radius: number = e.layer.getRadius();
      const feature = {
        type: 'Feature',
        properties: {
          id: generateId()
        },
        geometry: circleToPolygon([latLng.lng, latLng.lat], radius, 32)
      };
      onFinish(feature);
    }
  });
  drawer.enable();
};

type Props = WithStyles & RouteComponentProps<any>;

class NewFieldFlow extends React.Component<Props, State> {
  leafletRef: L.Map | null;

  constructor(props: Props) {
    super(props);
    this.state = {
      tempAoi: null,
      tempName: null,
      tempCrop: 'soja',
      selected: null,
      editedGeometry: null,
      showUploadKmzDialog: false,
      showDeleteDialog: false,
      lastAction: null,
      showUpdateDialog: false
    };
  }

  handleStartPolygon = () => {
    startPolygon(this.leafletRef, this.handleAoiUpdated);
  };

  handleStartCircle = () => {
    startCircle(this.leafletRef, this.handleAoiUpdated);
  };

  handleAoiUpdated = (tempAoi: Feature<Polygon, AoiProperties>) => {
    this.setState({ tempAoi });
  };

  handleRememberEdit = (editedGeometry: PolygonGeometry) => {
    this.setState({ editedGeometry });
  };

  handleDeleteField = (deleteFn: DeleteFn) => {
    deleteFn((this.state.selected as Field).id);
    this.clearSelectedField();
  };

  handleFinishEdit = (updateFn: UpdateFn, updateGeometry: boolean) => {
    const field = this.state.selected as Field;
    const geometry = this.state.editedGeometry as PolygonGeometry;
    const { tempName, tempCrop } = this.state;
    updateFn({
      id: field.id,
      name: updateGeometry ? field.name : (tempName as string),
      currentCrop: updateGeometry ? field.currentCrop : tempCrop,
      geometry: updateGeometry
        ? geometry
        : {
            type: field.geometry.type,
            coordinates: field.geometry.coordinates
          }
    });
    this.clearSelectedField();
    this.setState({ showUpdateDialog: false });
  };

  handleCreateCompleted = (
    field: Field,
    layout: LayoutTools,
    refetch?: () => void
  ) => {
    if (refetch) {
      refetch();
    }

    layout.turnOnNotifications();

    this.setState({
      lastAction: {
        type: 'create',
        field
      }
    });
  };

  saveTempField(mutateFn: MutateFn) {
    const {
      match: {
        params: { farmId }
      }
    } = this.props;
    const { tempAoi, tempName, tempCrop } = this.state;

    if (!tempAoi || !tempName || !tempCrop) {
      return;
    }

    mutateFn({
      name: tempName,
      farmId: farmId,
      geometry: tempAoi.geometry,
      currentCrop: tempCrop
    });

    this.clearTempField();
  }

  clearSelectedField() {
    this.setState({
      selected: null,
      editedGeometry: null,
      showDeleteDialog: false
    });
  }

  clearTempField() {
    this.setState({
      tempAoi: null,
      tempName: null,
      tempCrop: 'soja',
      showUpdateDialog: false
    });
  }

  showUploadKmzDialog() {
    this.setState({ showUploadKmzDialog: true });
  }

  handleKmzFields(mutateFn: MutateFn, fields: any[]) {
    const {
      match: {
        params: { farmId }
      }
    } = this.props;

    this.setState({ showUploadKmzDialog: false });

    for (let field of fields) {
      mutateFn({
        name: field.name,
        farmId: farmId,
        geometry: {
          type: 'Polygon',
          coordinates: field.geometry.coordinates
        },
        currentCrop: field.currentCrop || ''
      });
    }
  }

  renderCreateActionSnackbar() {
    const { lastAction } = this.state;

    const btn = (
      <LinkButton color="secondary" size="small" to="/notifications">
        Procesando Imágenes
      </LinkButton>
    );

    return (
      <Snackbar
        open={(lastAction && lastAction.type === 'create') || false}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => this.setState({ lastAction: null })}
        autoHideDuration={10 * 1000}
      >
        <SnackbarContent message={`el lote ha sido creado`} action={btn} />
      </Snackbar>
    );
  }

  renderActionSnackbar() {
    return this.renderCreateActionSnackbar();
    // TODO: add other types of snacks
  }

  renderSaveDialog(mutateFn: MutateFn) {
    const { classes } = this.props;
    const { tempAoi, tempName, tempCrop } = this.state;
    return (
      <Dialog open={!!tempAoi}>
        <DialogTitle>Guardar Lote</DialogTitle>
        <DialogContent style={{ width: 400 }}>
          <TextField
            label="Nombre de Lote"
            fullWidth={true}
            onChange={evt => this.setState({ tempName: evt.target.value })}
            value={tempName || undefined}
            className={classes.input}
          />
          <FormControl fullWidth={true} style={{ marginTop: 10 }}>
            <InputLabel htmlFor="current-crop">Cultivo Actual</InputLabel>
            <Select
              value={tempCrop}
              onChange={evt => this.setState({ tempCrop: evt.target.value })}
              inputProps={{ id: 'current-crop' }}
              fullWidth={true}
            >
              <MenuItem value="Algodón">Algodón</MenuItem>
              <MenuItem value="Alpiste">Alpiste</MenuItem>
              <MenuItem value="Arroz">Arroz</MenuItem>
              <MenuItem value="Arveja">Arveja</MenuItem>
              <MenuItem value="Avena">Avena</MenuItem>
              <MenuItem value="Barbecho">Barbecho</MenuItem>
              <MenuItem value="Caña de Azucar">Caña de Azucar</MenuItem>
              <MenuItem value="Cebada">Cebada</MenuItem>
              <MenuItem value="Centeno">Centeno</MenuItem>
              <MenuItem value="Colza">Colza</MenuItem>
              <MenuItem value="Cultivo de servicio">
                Cultivo de servicio
              </MenuItem>
              <MenuItem value="Garbanzo">Garbanzo</MenuItem>
              <MenuItem value="Girasol">Girasol</MenuItem>
              <MenuItem value="Hortalizas">Hortalizas</MenuItem>
              <MenuItem value="Maiz">Maiz</MenuItem>
              <MenuItem value="Mani">Mani</MenuItem>
              <MenuItem value="Mostaza">Mostaza</MenuItem>
              <MenuItem value="Papa">Papa</MenuItem>
              <MenuItem value="Pastura">Pastura</MenuItem>
              <MenuItem value="Poroto">Poroto</MenuItem>
              <MenuItem value="Soja">Soja</MenuItem>
              <MenuItem value="Sorgo">Sorgo</MenuItem>
              <MenuItem value="Trebol">Trebol</MenuItem>
              <MenuItem value="Trigo">Trigo</MenuItem>
              <MenuItem value="Verdeo">Verdeo</MenuItem>
              <MenuItem value="Vicia">Vicia</MenuItem>
              <MenuItem value="Otro">Otro</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => this.saveTempField(mutateFn)}>
            Crear Lote
          </Button>
          <Button color="default" onClick={() => this.clearTempField()}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  renderUpdateDialog(updateFn: UpdateFn) {
    const { classes } = this.props;
    const { showUpdateDialog, tempName, tempCrop } = this.state;
    return (
      <Dialog open={showUpdateDialog}>
        <DialogTitle>Guardar Lote</DialogTitle>
        <DialogContent style={{ width: 400 }}>
          <TextField
            label="Nombre de Lote"
            fullWidth={true}
            onChange={evt => this.setState({ tempName: evt.target.value })}
            value={tempName || undefined}
            className={classes.input}
          />
          <FormControl fullWidth={true} style={{ marginTop: 10 }}>
            <InputLabel htmlFor="current-crop">Cultivo Actual</InputLabel>
            <Select
              value={tempCrop}
              onChange={evt => this.setState({ tempCrop: evt.target.value })}
              inputProps={{ id: 'current-crop' }}
              fullWidth={true}
            >
              <MenuItem value="Algodón">Algodón</MenuItem>
              <MenuItem value="Alpiste">Alpiste</MenuItem>
              <MenuItem value="Arroz">Arroz</MenuItem>
              <MenuItem value="Arveja">Arveja</MenuItem>
              <MenuItem value="Avena">Avena</MenuItem>
              <MenuItem value="Barbecho">Barbecho</MenuItem>
              <MenuItem value="Caña de Azucar">Caña de Azucar</MenuItem>
              <MenuItem value="Cebada">Cebada</MenuItem>
              <MenuItem value="Centeno">Centeno</MenuItem>
              <MenuItem value="Colza">Colza</MenuItem>
              <MenuItem value="Cultivo de servicio">
                Cultivo de servicio
              </MenuItem>
              <MenuItem value="Garbanzo">Garbanzo</MenuItem>
              <MenuItem value="Girasol">Girasol</MenuItem>
              <MenuItem value="Hortalizas">Hortalizas</MenuItem>
              <MenuItem value="Maiz">Maiz</MenuItem>
              <MenuItem value="Mani">Mani</MenuItem>
              <MenuItem value="Mostaza">Mostaza</MenuItem>
              <MenuItem value="Papa">Papa</MenuItem>
              <MenuItem value="Pastura">Pastura</MenuItem>
              <MenuItem value="Poroto">Poroto</MenuItem>
              <MenuItem value="Soja">Soja</MenuItem>
              <MenuItem value="Sorgo">Sorgo</MenuItem>
              <MenuItem value="Trebol">Trebol</MenuItem>
              <MenuItem value="Trigo">Trigo</MenuItem>
              <MenuItem value="Verdeo">Verdeo</MenuItem>
              <MenuItem value="Vicia">Vicia</MenuItem>
              <MenuItem value="Otro">Otro</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            onClick={() => this.handleFinishEdit(updateFn, false)}
          >
            Guardar Cambios
          </Button>
          <Button color="default" onClick={() => this.clearTempField()}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  renderDeleteDialog(deleteFn: DeleteFn) {
    const { showDeleteDialog } = this.state;
    return (
      <Dialog open={showDeleteDialog}>
        <DialogTitle>¿Está seguro que desea eliminar el lote?</DialogTitle>
        <DialogActions>
          <Button color="default" onClick={() => this.clearSelectedField()}>
            No
          </Button>
          <Button
            color="primary"
            onClick={() => this.handleDeleteField(deleteFn)}
          >
            Si
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  renderAoiOverlay() {
    const { tempAoi } = this.state;
    if (!tempAoi) {
      return null;
    }

    return <GeoJSON key={tempAoi.properties.id} data={tempAoi} zIndex={2} />;
  }

  renderFieldMenu(updateFn: UpdateFn, field: Field) {
    const { classes } = this.props;
    const { editedGeometry } = this.state;
    return (
      <List
        className={classes.list}
        subheader={<ListSubheader>Lote {field.name}</ListSubheader>}
      >
        <ListItem
          button={true}
          onClick={() =>
            this.setState({
              showUpdateDialog: true,
              tempCrop: field.currentCrop,
              tempName: field.name
            })
          }
        >
          <ListItemIcon>
            <CreateIcon />
          </ListItemIcon>
          <ListItemText primary="Modificar datos" />
        </ListItem>
        <Divider />
        <ListItem
          button={true}
          disabled={!editedGeometry}
          onClick={() => this.handleFinishEdit(updateFn, true)}
        >
          <ListItemIcon>
            <SaveIcon />
          </ListItemIcon>
          <ListItemText primary="Guardar Cambios" />
        </ListItem>
        <Divider />
        <ListItem
          button={true}
          onClick={() => this.setState({ showDeleteDialog: true })}
        >
          <ListItemIcon>
            <ClearIcon />
          </ListItemIcon>
          <ListItemText primary="Eliminar Lote" />
        </ListItem>
      </List>
    );
  }

  render() {
    const {
      classes,
      match: {
        params: { farmId }
      }
    } = this.props;
    const { selected } = this.state;

    return (
      <LayoutToolsProvider>
        {layout => (
          <FarmProvider farmId={farmId} includeFields={true}>
            {(farm, loading, refetch) => (
              <NewFieldProvider
                onCompleted={f => this.handleCreateCompleted(f, layout, refetch)}
              >
                {(mutateFn, executing, newField) => (
                  <UpdateFieldProvider onCompleted={f => refetch && refetch()}>
                    {updateFn => (
                      <DeleteFieldProvider
                        onCompleted={f => refetch && refetch()}
                      >
                        {deleteFn => (
                          <>
                            {this.renderActionSnackbar()}
                            {this.renderSaveDialog(mutateFn)}
                            <main className={classes.root}>
                              <SidePanel>
                                <div style={{display: 'flex'}}>
                                  <div style={{flex: 0, marginRight: 16, alignItems: 'center'}}>
                                    <IconButton onClick={() => this.props.history.goBack()}>
                                      <BackIcon />
                                    </IconButton>
                                  </div>
                                  <div style={{flex: 1}}>
                                    {farm && (
                                      <T variant="subheading">{farm.name}</T>
                                    )}
                                    <T variant="headline" gutterBottom={true}>
                                      Definir Lotes
                                    </T>
                                  </div>
                                </div>
                                <T variant="subheading" paragraph={true}>
                                  Utilice esta seccion para definir los lotes de
                                  su campo. Una vez que haya terminado, haga
                                  clic en "Finalizar" para navegar la
                                  informacion de sus lotes.
                                </T>
                                <Button
                                  fullWidth={true}
                                  variant="raised"
                                  onClick={this.handleStartPolygon}
                                  className={classes.button}
                                  color="primary"
                                >
                                  <PolygonIcon />
                                  &nbsp;Nuevo Lote Poligono
                                </Button>
                                <Button
                                  fullWidth={true}
                                  variant="raised"
                                  onClick={this.handleStartCircle}
                                  className={classes.button}
                                  color="primary"
                                >
                                  <CircleIcon />
                                  &nbsp;Nuevo Lote Circular
                                </Button>
                                <Button
                                  fullWidth={true}
                                  variant="raised"
                                  onClick={() => this.showUploadKmzDialog()}
                                  className={classes.button}
                                >
                                  <CloudUploadIcon />
                                  &nbsp;Importar Archivo KML/KMZ
                                </Button>
                                {selected &&
                                  this.renderFieldMenu(updateFn, selected)}
                                <LinkButton
                                  fullWidth={true}
                                  variant="raised"
                                  className={classes.button}
                                  to={`/farms/${farmId}`}
                                >
                                  Finalizar
                                </LinkButton>
                              </SidePanel>

                              {farm && (
                                <FarmMap
                                  farm={farm}
                                  className={classes.map}
                                  selectedId={selected && selected.id}
                                  keyboard={
                                    !this.state.tempAoi &&
                                    !this.state.showUploadKmzDialog
                                  }
                                  onFieldClick={args =>
                                    this.setState({ selected: args.field })
                                  }
                                  leafletRef={ref => (this.leafletRef = ref)}
                                  editSelected={true}
                                  onSelectedEdit={this.handleRememberEdit}
                                >
                                  {this.renderAoiOverlay()}
                                </FarmMap>
                              )}
                            </main>

                            <UploadKmzDialog
                              open={this.state.showUploadKmzDialog}
                              onClose={() =>
                                this.setState({ showUploadKmzDialog: false })
                              }
                              onSubmit={fields =>
                                this.handleKmzFields(mutateFn, fields)
                              }
                            />

                            {this.renderDeleteDialog(deleteFn)}
                            {this.renderUpdateDialog(updateFn)}
                          </>
                        )}
                      </DeleteFieldProvider>
                    )}
                  </UpdateFieldProvider>
                )}
              </NewFieldProvider>
            )}
          </FarmProvider>
        )}
      </LayoutToolsProvider>
    );
  }
}

export default withStyles(styles)(withRouter(NewFieldFlow));



// WEBPACK FOOTER //
// ./src/containers/DefineFieldsFlow.tsx